import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, UserCircle2, Sparkles, LogIn, LogOut, ShieldCheck, RotateCcw, Heart, Camera } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useProfile } from "../context/ProfileContext";
import { useAuth, useIsAdmin } from "../context/AuthContext";
import { useWishlistCount } from "./WishlistButton";
import { TrustBadge } from "./TrustBadge";
import { WelcomeBackPill } from "./WelcomeBackPill";
import { VisualSearchModal } from "./VisualSearchModal";
import { DownloadAppButton } from "./DownloadAppButton";

export const Navbar = () => {
    const { count, setDrawerOpen } = useCart();
    const { profile, profileId } = useProfile();
    const { user, logout } = useAuth();
    const isAdmin = useIsAdmin();
    const wishlistCount = useWishlistCount();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [visualSearchOpen, setVisualSearchOpen] = React.useState(false);

    const links = [
        { to: "/search", label: "SEARCH" },
        { to: "/matches", label: "MY FITS" },
        { to: "/outfits", label: "LOOKS" },
        { to: "/shop", label: "SHOP" },
    ];

    return (
        <header
            data-testid="navbar"
            className="sticky top-0 z-40 bg-[#FDFBF7] border-b-2 border-black"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex flex-col leading-none"
                >
                    <span className="font-display text-2xl md:text-3xl font-black tracking-tighter">
                        FITS<span className="bg-black text-[#CCFF00] px-1 ml-1">ME</span>
                    </span>
                    <span
                        data-testid="logo-tagline"
                        className="block text-center font-mono text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-black/70 mt-1"
                    >
                        before i buy
                    </span>
                </Link>
                <WelcomeBackPill />

                <nav className="hidden md:flex items-center gap-6">
                    {links.map((l) => (
                        <Link
                            key={l.label}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={`text-xs font-bold tracking-widest hover:text-[#FF00FF] transition-colors ${
                                location.pathname + location.search === l.to
                                    ? "underline underline-offset-4"
                                    : ""
                            }`}
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    {user && (
                        <Link
                            to="/returns"
                            data-testid="nav-returns"
                            className="hidden sm:inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-2 shadow-brutal-sm btn-press font-mono text-xs uppercase tracking-widest"
                            aria-label="My returns"
                            title="My returns"
                        >
                            <RotateCcw className="w-4 h-4" />
                            <span>Returns</span>
                        </Link>
                    )}
                    {isAdmin && (
                        <Link
                            to="/admin/profiles"
                            data-testid="nav-admin"
                            className="hidden sm:inline-flex items-center gap-2 border-2 border-black bg-black text-[#CCFF00] px-3 py-2 shadow-brutal-sm btn-press font-mono text-xs uppercase tracking-widest"
                            aria-label="Admin"
                        >
                            <ShieldCheck className="w-4 h-4" />
                            <span>Admin</span>
                        </Link>
                    )}
                    {user ? (
                        <button
                            onClick={logout}
                            data-testid="nav-signout"
                            className="hidden sm:inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-2 shadow-brutal-sm btn-press font-mono text-xs uppercase tracking-widest"
                            aria-label="Sign out"
                            title={user.email}
                        >
                            <LogOut className="w-4 h-4" />
                            <span className="max-w-[100px] truncate">{user.email.split("@")[0]}</span>
                        </button>
                    ) : (
                        <Link
                            to="/login"
                            data-testid="nav-signin"
                            className="hidden sm:inline-flex items-center gap-2 border-2 border-black bg-[#CCFF00] px-3 py-2 shadow-brutal-sm btn-press font-mono text-xs uppercase tracking-widest"
                            aria-label="Sign in"
                        >
                            <LogIn className="w-4 h-4" />
                            <span>Sign in</span>
                        </Link>
                    )}
                    <DownloadAppButton className="hidden sm:inline-flex" />
                    <Link
                        to="/profile"
                        data-testid="profile-link"
                        className="hidden sm:inline-flex relative border-2 border-black bg-white px-3 py-2 shadow-brutal-sm btn-press items-center gap-2 font-mono text-xs uppercase tracking-widest"
                        aria-label="Profile"
                    >
                        {profileId ? (
                            <>
                                <UserCircle2 className="w-4 h-4" />
                                <span className="max-w-[80px] truncate">{profile?.name?.split(" ")[0] || "Profile"}</span>
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-4 h-4" />
                                <span>Get matched</span>
                            </>
                        )}
                    </Link>
                    {user && <TrustBadge />}
                    <button
                        onClick={() => setVisualSearchOpen(true)}
                        data-testid="nav-visual-search"
                        aria-label="Visual search — snap a photo to find similar items"
                        title="Shop this look (AI)"
                        className="border-2 border-black bg-[#CCFF00] p-2 shadow-brutal-sm btn-press"
                    >
                        <Camera className="w-5 h-5" />
                    </button>
                    <Link
                        to="/wishlist"
                        data-testid="nav-wishlist"
                        className="relative border-2 border-black bg-white p-2 shadow-brutal-sm btn-press"
                        aria-label="Wishlist"
                        title="Wishlist"
                    >
                        <Heart className={`w-5 h-5 ${wishlistCount > 0 ? "fill-[#FF00FF] text-[#FF00FF]" : ""}`} />
                        {wishlistCount > 0 && (
                            <span
                                data-testid="wishlist-count"
                                className="absolute -top-2 -right-2 bg-[#FF00FF] text-white text-[10px] font-bold border-2 border-black w-5 h-5 flex items-center justify-center"
                            >
                                {wishlistCount}
                            </span>
                        )}
                    </Link>
                    <button
                        data-testid="cart-toggle"
                        onClick={() => setDrawerOpen(true)}
                        className="relative border-2 border-black bg-white p-2 shadow-brutal-sm btn-press"
                        aria-label="Open cart"
                    >
                        <ShoppingBag className="w-5 h-5" />
                        {count > 0 && (
                            <span
                                data-testid="cart-count"
                                className="absolute -top-2 -right-2 bg-[#FF00FF] text-white text-[10px] font-bold border-2 border-black w-5 h-5 flex items-center justify-center"
                            >
                                {count}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setMenuOpen((v) => !v)}
                        className="md:hidden border-2 border-black bg-white p-2 shadow-brutal-sm"
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>
            {menuOpen && (
                <div className="md:hidden border-t-2 border-black bg-white">
                    <div className="flex flex-col p-4 gap-3">
                        {links.map((l) => (
                            <Link
                                key={l.label}
                                to={l.to}
                                onClick={() => setMenuOpen(false)}
                                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                className="text-sm font-bold tracking-widest"
                            >
                                {l.label}
                            </Link>
                        ))}
                        <Link
                            to="/profile"
                            onClick={() => setMenuOpen(false)}
                            data-testid="mobile-nav-profile"
                            className="text-sm font-bold tracking-widest"
                        >
                            PROFILE
                        </Link>
                        <Link
                            to="/wishlist"
                            onClick={() => setMenuOpen(false)}
                            data-testid="mobile-nav-wishlist"
                            className="text-sm font-bold tracking-widest"
                        >
                            WISHLIST {wishlistCount > 0 && <span className="text-[#FF00FF]">({wishlistCount})</span>}
                        </Link>
                        <div onClick={() => setMenuOpen(false)} data-testid="mobile-nav-download-app">
                            <DownloadAppButton variant="block" />
                        </div>
                        {isAdmin && (
                            <Link
                                to="/admin/profiles"
                                onClick={() => setMenuOpen(false)}
                                data-testid="mobile-nav-admin"
                                className="text-sm font-bold tracking-widest"
                            >
                                ADMIN
                            </Link>
                        )}
                        {user ? (
                            <button
                                onClick={() => { setMenuOpen(false); logout(); }}
                                data-testid="mobile-nav-signout"
                                className="text-sm font-bold tracking-widest text-left"
                            >
                                SIGN OUT ({user.email.split("@")[0]})
                            </button>
                        ) : (
                            <Link
                                to="/login"
                                onClick={() => setMenuOpen(false)}
                                data-testid="mobile-nav-signin"
                                className="text-sm font-bold tracking-widest"
                            >
                                SIGN IN
                            </Link>
                        )}
                    </div>
                </div>
            )}
            {visualSearchOpen && (
                <VisualSearchModal onClose={() => setVisualSearchOpen(false)} />
            )}
        </header>
    );
};
